import { Fragment, useEffect, useRef, useState } from "react";
import { EditorContent, Node, mergeAttributes, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
  Bold,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  MessageCircleHeart,
  ListOrdered,
  Smile,
  Sticker,
  Underline as UnderlineIcon,
} from "lucide-react";
import { ActionButton } from "@/components/ui/action-button";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { uploadComunidadMedia } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { BRAND_TEXT_COLORS, TEXT_SIZES, VOCES_ATTR, isRichHtml, plainTextToHtml } from "../richText";

/** Cómo se muestra una imagen dentro del contenido (ver estilos en `RichContent`). */
type ImageVariant = "banner" | "inline" | "sticker";

/** Tamaño de una frase del texto común ("A+" / "A++"), guardado como `font-size` en el
 *  mismo `<span style>` que el color. Solo los valores de TEXT_SIZES pasan el sanitizado. */
const ComunidadTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).style.fontSize || null,
        renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
      },
    };
  },
});

/** Imagen con su "variante" (banner a todo el ancho, imagen normal o sticker chico). */
const ComunidadImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      variant: {
        default: "inline",
        parseHTML: (el) => el.getAttribute("data-variant") || "inline",
        renderHTML: (attrs) => ({ "data-variant": attrs.variant }),
      },
    };
  },
});

/**
 * Bloque "Voces de la Red": marca dónde va el renglón para responder la consigna dentro del
 * newsletter. En el editor se ve como un recuadro; en la pantalla se reemplaza por la caja de
 * respuesta (ver `RichContent.renderVoces`). Se puede mover arrastrándolo o borrarlo.
 */
const VocesBlock = Node.create({
  name: "vocesDeLaRed",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML: () => [{ tag: `div[${VOCES_ATTR}]` }],
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { [VOCES_ATTR]: "true" }),
    "💬 Voces de la Red — acá va el renglón para que respondan la consigna",
  ],
});

const EMOJIS = [
  "😊", "😄", "🥰", "😍", "🤗", "😌", "🙌", "👏", "💪", "🙏",
  "💜", "🧡", "💛", "💚", "💙", "✨", "🌟", "🎉", "🎊", "🌈",
  "🌱", "🌸", "🌻", "☀️", "🌙", "☕", "🧉", "📚", "📝", "📅",
  "🎓", "🧠", "💡", "🗣️", "👀", "📌", "✅", "⚠️", "❤️‍🩹", "🫶",
  "👋", "🤝", "💬", "🎯", "🔥", "🌿", "🍃", "⏳", "🕰️", "🚪",
];

const MAX_SIDE_PX = 1600;

/**
 * Achica la imagen en el navegador antes de subirla (lado mayor 1600 px, WEBP). Los GIF se
 * suben tal cual para no perder la animación de los memes.
 */
async function shrinkImage(file: File): Promise<{ blob: Blob; name: string }> {
  if (file.type === "image/gif") return { blob: file, name: file.name };
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.85));
  if (!blob) return { blob: file, name: file.name };
  return { blob, name: file.name.replace(/\.[^.]+$/, "") + ".webp" };
}

interface RichTextEditorProps {
  /** HTML (o texto plano viejo: se convierte solo al abrir). */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Para los ids de los controles (tests y accesibilidad). */
  idPrefix: string;
  /** Muestra el botón para ubicar el bloque "Voces de la Red" (solo el newsletter). */
  allowVoces?: boolean;
}

type Panel = null | "link" | "emoji" | "image" | "sticker";

const toolbarBtn = "h-8 min-w-8 px-2 text-[#2D2049]";
const activeCls = "bg-[#EDE6FA] text-[#665090]";

/**
 * Editor visual del newsletter y las novedades de Comunidad: títulos, negrita/cursiva/
 * subrayado, los colores fijos de la marca, listas, links, emojis, imágenes (pegando un link
 * o subiéndolas) y stickers/memes. Lo que se ve acá es lo que van a ver las psicólogas.
 */
export const RichTextEditor = ({ value, onChange, placeholder, idPrefix, allowVoces = false }: RichTextEditorProps) => {
  const [panel, setPanel] = useState<Panel>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2] }, codeBlock: false, code: false, horizontalRule: false }),
      Underline,
      ComunidadTextStyle,
      Color,
      Link.configure({ openOnClick: false, autolink: true }),
      ComunidadImage.configure({ inline: false, allowBase64: false }),
      VocesBlock,
    ],
    content: isRichHtml(value) ? value : plainTextToHtml(value),
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
    editorProps: {
      attributes: {
        class:
          "min-h-[160px] px-3 py-2 text-sm leading-relaxed text-[#2D2049] outline-none " +
          "[&_h1]:text-xl [&_h1]:font-extrabold [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-[#665090] " +
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-[#665090] [&_a]:underline " +
          "[&_img]:my-2 [&_img]:max-h-[220px] [&_img]:rounded-lg [&_img[data-variant=sticker]]:max-h-[120px] " +
"[&_img.ProseMirror-selectednode]:ring-2 [&_img.ProseMirror-selectednode]:ring-[#665090] " +
          "[&_div[data-voces-de-la-red]]:my-3 [&_div[data-voces-de-la-red]]:cursor-grab [&_div[data-voces-de-la-red]]:rounded-xl [&_div[data-voces-de-la-red]]:border-2 [&_div[data-voces-de-la-red]]:border-dashed [&_div[data-voces-de-la-red]]:border-[#8475B3] " +
          "[&_div[data-voces-de-la-red]]:bg-[#F5F1FF] [&_div[data-voces-de-la-red]]:px-4 [&_div[data-voces-de-la-red]]:py-3 [&_div[data-voces-de-la-red]]:text-center [&_div[data-voces-de-la-red]]:text-sm [&_div[data-voces-de-la-red]]:font-semibold [&_div[data-voces-de-la-red]]:text-[#665090] " +
          "[&_div.ProseMirror-selectednode]:ring-2 [&_div.ProseMirror-selectednode]:ring-[#665090]",
        "aria-label": placeholder ?? "Contenido",
      },
    },
  });

  // Si el valor cambia desde afuera (ej. se limpia el formulario al guardar), sincronizar.
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if (value !== current && (value === "" || !editor.isFocused)) {
      editor.commands.setContent(isRichHtml(value) ? value : plainTextToHtml(value), false);
    }
  }, [value, editor]);

  if (!editor) {
    return <div className="flex justify-center py-6"><CircularProgress /></div>;
  }

  const insertImage = (src: string, variant: ImageVariant) => {
    editor.chain().focus().insertContent({ type: "image", attrs: { src, alt: "", variant } }).run();
    setPanel(null);
    setImageUrl("");
  };

  const insertFromUrl = (variant: ImageVariant) => {
    const url = imageUrl.trim();
    if (!/^https:\/\//i.test(url)) {
      toastUtils.error("El link de la imagen tiene que empezar con https://");
      return;
    }
    insertImage(url, variant);
  };

  const onFile = async (file: File | undefined, variant: ImageVariant) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toastUtils.error("Elegí una imagen (JPG, PNG, GIF o WEBP)");
      return;
    }
    setUploading(true);
    try {
      const { blob, name } = await shrinkImage(file);
      const { url } = await uploadComunidadMedia(blob, name);
      insertImage(url, variant);
    } catch (e) {
      toastUtils.error(e instanceof Error ? e.message : "No se pudo subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else if (!/^https?:\/\//i.test(url)) {
      toastUtils.error("El link tiene que empezar con https://");
      return;
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setPanel(null);
    setLinkUrl("");
  };

  const toggle = (p: Exclude<Panel, null>) => setPanel((cur) => (cur === p ? null : p));

  // Un solo bloque por edición: si ya está, se avisa en vez de duplicarlo.
  const insertVoces = () => {
    let exists = false;
    editor.state.doc.descendants((node) => {
      if (node.type.name === "vocesDeLaRed") exists = true;
    });
    if (exists) {
      toastUtils.info("El renglón de Voces de la Red ya está en el newsletter. Podés moverlo arrastrándolo o borrarlo.");
      return;
    }
    // Con un párrafo vacío debajo, para poder seguir escribiendo después del bloque.
    editor.chain().focus().insertContent([{ type: "vocesDeLaRed" }, { type: "paragraph" }]).run();
  };
  const imageVariant: ImageVariant = panel === "sticker" ? "sticker" : "inline";

  return (
    <div className="rounded-lg border border-[#C9BCE8] bg-white">
      <Toolbar editor={editor} onToggle={toggle} panel={panel} onInsertVoces={allowVoces ? insertVoces : undefined} />

      {panel === "link" && (
        <div className="flex flex-wrap items-center gap-2 border-b border-[#E3D9FF] bg-[#FAF8FF] px-3 py-2">
          <input
            id={`${idPrefix}-link-url`}
            className="min-w-0 flex-1 rounded-lg border border-[#C9BCE8] px-2 py-1 text-xs"
            placeholder="https://… (vacío para sacar el link)"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyLink())}
          />
          <Button size="sm" onClick={applyLink}>Aplicar</Button>
        </div>
      )}

      {panel === "emoji" && (
        <div className="grid grid-cols-10 gap-1 border-b border-[#E3D9FF] bg-[#FAF8FF] px-3 py-2">
          {EMOJIS.map((em) => (
            <ActionButton
              key={em}
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-lg"
              aria-label={`Insertar ${em}`}
              onClick={() => {
                editor.chain().focus().insertContent(em).run();
              }}
            >
              {em}
            </ActionButton>
          ))}
        </div>
      )}

      {(panel === "image" || panel === "sticker") && (
        <div className="flex flex-col gap-2 border-b border-[#E3D9FF] bg-[#FAF8FF] px-3 py-2 text-xs text-[#4A3D73]">
          <span className="font-semibold">
            {panel === "sticker" ? "Sticker o meme (se ve chico)" : "Imagen"}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? "Subiendo…" : "Subir desde mi compu"}
            </Button>
            <input
              ref={fileRef}
              id={`${idPrefix}-file`}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0], imageVariant)}
            />
            <span>o</span>
            <input
              id={`${idPrefix}-image-url`}
              className="min-w-0 flex-1 rounded-lg border border-[#C9BCE8] px-2 py-1 text-xs"
              placeholder="Pegá el link de una imagen o GIF (https://…)"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
            {panel === "image" ? (
              <>
                <Button size="sm" variant="outline" onClick={() => insertFromUrl("inline")}>Insertar</Button>
                <Button size="sm" variant="outline" onClick={() => insertFromUrl("banner")}>Como banner</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => insertFromUrl("sticker")}>Insertar</Button>
            )}
          </div>
          {panel === "image" && (
            <span className="text-[11px] text-gray-400">
              Para que una imagen subida quede como banner a todo el ancho, seleccionala y tocá «Banner» abajo.
            </span>
          )}
        </div>
      )}

      <EditorContent editor={editor} id={`${idPrefix}-content`} />

      {editor.isActive("image") && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[#E3D9FF] bg-[#FAF8FF] px-3 py-1.5 text-xs text-[#4A3D73]">
          <span className="font-semibold">Imagen seleccionada:</span>
          {(["banner", "inline", "sticker"] as const).map((v) => (
            <ActionButton
              key={v}
              variant="outline"
              size="sm"
              className={editor.getAttributes("image").variant === v ? activeCls : ""}
              onClick={() => editor.chain().focus().updateAttributes("image", { variant: v }).run()}
            >
              {v === "banner" ? "Banner" : v === "inline" ? "Normal" : "Sticker"}
            </ActionButton>
          ))}
        </div>
      )}
    </div>
  );
};

const Toolbar = ({
  editor,
  onToggle,
  panel,
  onInsertVoces,
}: {
  editor: Editor;
  onToggle: (p: Exclude<Panel, null>) => void;
  panel: Panel;
  onInsertVoces?: () => void;
}) => {
  const btn = (label: string, active: boolean, onClick: () => void, child: React.ReactNode) => (
    <ActionButton
      variant="ghost"
      size="sm"
      aria-label={label}
      title={label}
      className={`${toolbarBtn} ${active ? activeCls : ""}`}
      onClick={onClick}
    >
      {child}
    </ActionButton>
  );
  const sep = <span className="mx-0.5 h-5 w-px bg-[#E3D9FF]" aria-hidden="true" />;
  const currentColor = (editor.getAttributes("textStyle").color as string | undefined)?.toLowerCase();
  const currentSize = editor.getAttributes("textStyle").fontSize as string | undefined;

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-[#E3D9FF] bg-[#FAF8FF] px-2 py-1.5">
      {btn("Título", editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), <span className="text-xs font-bold">H1</span>)}
      {btn("Subtítulo", editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <span className="text-xs font-bold">H2</span>)}
      {sep}
      {btn("Negrita", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), <Bold size={15} />)}
      {btn("Cursiva", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), <Italic size={15} />)}
      {btn("Subrayado", editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon size={15} />)}
      {sep}
      {BRAND_TEXT_COLORS.map((c) => (
        <ActionButton
          key={c.hex}
          variant="ghost"
          size="icon"
          aria-label={`Color ${c.name}`}
          title={c.name}
          className={`h-7 w-7 ${currentColor === c.hex.toLowerCase() ? activeCls : ""}`}
          onClick={() => editor.chain().focus().setColor(c.hex).run()}
        >
          <span className="block h-4 w-4 rounded-full border-2 border-white shadow-[0_0_0_1px_#CFC4EA]" style={{ background: c.hex }} />
        </ActionButton>
      ))}
      {btn("Sin color", false, () => editor.chain().focus().unsetColor().run(), <span className="text-[11px] text-gray-500">Aa</span>)}
      {sep}
      {btn(
        "Texto normal",
        !currentSize,
        () => editor.chain().focus().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
        <span className="text-xs font-semibold">A</span>,
      )}
      {TEXT_SIZES.map((sz) => (
        <Fragment key={sz.value}>
          {btn(
            sz.name,
            currentSize === sz.value,
            () => editor.chain().focus().setMark("textStyle", { fontSize: sz.value }).run(),
            <span className={sz.label === "A+" ? "text-sm font-semibold" : "text-base font-semibold"}>{sz.label}</span>,
          )}
        </Fragment>
      ))}
      {sep}
      {btn("Lista", editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), <List size={15} />)}
      {btn("Lista numerada", editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered size={15} />)}
      {btn("Link", editor.isActive("link") || panel === "link", () => onToggle("link"), <LinkIcon size={15} />)}
      {sep}
      {btn("Emojis", panel === "emoji", () => onToggle("emoji"), <Smile size={15} />)}
      {btn("Imagen", panel === "image", () => onToggle("image"), <><ImageIcon size={15} /><span className="ml-1 text-xs">Imagen</span></>)}
      {btn("Sticker o meme", panel === "sticker", () => onToggle("sticker"), <><Sticker size={15} /><span className="ml-1 text-xs">Sticker</span></>)}
      {onInsertVoces && (
        <>
          {sep}
          {btn("Ubicar Voces de la Red", false, onInsertVoces, <><MessageCircleHeart size={15} /><span className="ml-1 text-xs">Voces de la Red</span></>)}
        </>
      )}
    </div>
  );
};

export default RichTextEditor;
